import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { coerce } from 'semver';

import { assertExists } from 'common';
import { User } from 'models';

import {
  ThemeMode,
  UserUiSettings,
  UserUiSettingsSet,
  createEmptyUserUiSettingsSet,
} from '../../models';
import { AppConfigQuery } from '../../modules/app-config';
import { NotificationService } from '../../services/notification.service';
import { ArchivesQuery } from '../archives/archives.query';
import { DatabasesQuery } from '../databases/databases.query';
import { SearchesQuery } from '../searches/searches.query';
import { ApplicationQuery } from './application.query';
import {
  ApplicationState,
  ApplicationStore,
  createDefaultUserUiSettings,
} from './application.store';

/**
 * Application service.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationService {
  constructor(
    private databasesQuery: DatabasesQuery,
    private archivesQuery: ArchivesQuery,
    private searchesQuery: SearchesQuery,
    private applicationStore: ApplicationStore,
    private applicationQuery: ApplicationQuery,
    private notify: NotificationService,
    private appConfigQuery: AppConfigQuery,
    private logger: NGXLogger
  ) {}

  /**
   * Adds the provided filename to the history and ensures that only the last 5 entries are stored.
   *
   * @param filename Filename to be added to the history.
   */
  addFilenameToEnhancedExportHistory(filename: string): void {
    this.applicationStore.update(({ enhancedExportFilenameHistory }) => {
      let filenames = [...enhancedExportFilenameHistory];
      if (filenames.includes(filename)) {
        return {
          enhancedExportFilenameHistory: filenames,
        };
      }

      filenames.push(filename);
      // Ensure only the last 5 items are in the array
      filenames = filenames.slice(-5);
      return {
        enhancedExportFilenameHistory: filenames,
      };
    });
  }

  /**
   * Apply User UI settings.
   *
   * Updates the store with the new settings, and tracks which settings have been changed by the user.
   *
   * @param settings New desired settings.
   * @param userInitiated Whether the change was initiated by the user. Defaults to true.
   */
  applyUserUiSettings(
    settings: Partial<UserUiSettings>,
    userInitiated: boolean = true
  ): void {
    // Get the previous settings to determine what has changed.
    const previousSettings =
      this.applicationQuery.getValue() as Partial<UserUiSettings>;
    // Loop the new settings with may be a partial state of the full settings,
    // and compare for changed values and store them to another object.
    const changedSettings: Partial<UserUiSettingsSet> = {};
    for (const key of Object.keys(settings) as (keyof UserUiSettings)[]) {
      if (settings[key] !== previousSettings[key]) {
        changedSettings[key] = true;
      }
    }
    // Filter out any forced settings that should not be applied if this is a user initiated change.
    if (userInitiated) {
      const forcedSettings = this.appConfigQuery.userUiSettingsForced;
      for (const key of Object.keys(
        forcedSettings
      ) as (keyof UserUiSettings)[]) {
        if (key in changedSettings) {
          this.logger.debug(
            `Not applying user setting '${key}' as it is forced by app config.`
          );
          this.notify.warning('SETTING_KEY_IS_MANAGED_BY_YOUR_ADMINISTRATOR', {
            key: key,
          });
          delete settings[key];
        }
      }
    }
    this.logger.debug('Applying user UI settings changes:', changedSettings);
    // Update the store with only the changed settings.
    this.applicationStore.update(settings);
    // If settings were changed by the user update the userUiSettingsSetByUser
    // to track which settings have been set by the user.
    if (userInitiated && Object.keys(changedSettings).length > 0) {
      this.applicationStore.update((state) => {
        const newSetByUser = {
          ...state.userUiSettingsSetByUser,
          ...changedSettings,
        };
        return { userUiSettingsSetByUser: newSetByUser };
      });
    }
  }

  /**
   * Clear the open document.
   */
  clearOpenDocument(): void {
    this.applicationStore.update({ openDocument: undefined });
  }

  /**
   * Clear PDF Preview URL.
   */
  clearPdfPreviewUrl() {
    this.applicationStore.update({
      pdfPreviewUrl: '',
      pdfPreviewContentSearchTerm: '',
    });
  }

  /**
   * Clear the current user.
   */
  clearUser() {
    this.applicationStore.update({ user: undefined });
  }

  /** Clear workflow button overrides. */
  clearUserActionButtonOverrides(): void {
    this.applicationStore.update({ userActionButtonStateOverrides: {} });
  }

  /**
   * Reset user settings to default.
   */
  resetUserSettingsToDefault(): void {
    const defaultSettings = {
      ...createDefaultUserUiSettings(),
      userUiSettingsSetByUser: createEmptyUserUiSettingsSet(), // Ensure defaults are used since the user is reverting their settings.
    };
    this.applicationStore.update(defaultSettings);
    const overridedSettings = this.appConfigQuery.userUiSettingsDefaults;
    if (Object.keys(overridedSettings).length > 0) {
      // Apply settings overrides if present.
      this.applyUserUiSettings(overridedSettings, false);
    }
    const forcedOverridedSettings = this.appConfigQuery.userUiSettingsForced;
    if (Object.keys(forcedOverridedSettings).length > 0) {
      // Apply forced settings overrides if present.
      this.applyUserUiSettings(forcedOverridedSettings, false);
    }
  }

  /**
   * Sets the current API version.
   *
   * @param apiVersion API version.
   */
  setApiVersion(apiVersion: string) {
    const semanticVersion = coerce(apiVersion);
    assertExists(
      semanticVersion,
      'API version could not be coerced to semver.'
    );
    this.applicationStore.update({ apiVersion: semanticVersion.toString() });
  }

  /**
   * Sets {@link ApplicationState.autoSave}
   * @param on New desired state.
   */
  setAutoSave(on: boolean): void {
    this.applicationStore.update({ autoSave: on });
  }

  /**
   * Set if Dark Mode is enabled.
   *
   * @param on New desired state.
   */
  setDarkMode(on: boolean) {
    this.logger.debug(`Setting dark mode to: ${on}`);
    const themeMode = on ? ThemeMode.dark : ThemeMode.light;
    this.applicationStore.update({ themeMode });
  }

  /**
   * Set if compact mode is enabled.
   *
   * @param on New desired state.
   */
  setUseCompactMode(on: boolean) {
    this.logger.debug(`Setting compact mode to: ${on}`);
    this.applicationStore.update({ useCompactMode: on });
  }

  /**
   * Set the GSE API Url.
   *
   * @param url New GSE Api Url.
   */
  setGSEUrl(url: string): void {
    this.logger.debug(`Setting GSE URL to: ${url}`);
    this.applicationStore.update({ gseApiConfig: { apiUrl: url } });
  }

  /**
   * Sets the indexerSidebarOpen state.
   *
   * @param open Whether the sidebar should be open.
   */
  setIndexerSidebarOpen(open: boolean): void {
    this.applicationStore.update({ indexerSidebarOpen: open });
  }

  /**
   * Set the instance URL.
   *
   * @param instanceUrl New instance URL.
   */
  setInstanceUrl(instanceUrl: string) {
    // Update the instance URL in the store, if it has changed.
    this.applicationStore.update((state) => {
      if (state.instanceUrl !== instanceUrl) {
        return { instanceUrl };
      }
      return state;
    });
  }

  /**
   * Set if system notifcations are enabled.
   *
   * @param on New desired state.
   */
  setNotifications(on: boolean) {
    this.applicationStore.update({ notifications: on });
  }

  /**
   * Sets the open document to an archive document.
   *
   * @param documentId Document Id.
   * @param documentSecureId Document Secure Id.
   * @param databaseId Database Id.
   * @param archiveId Archive Id.
   * @param searchId Search Id.
   * @param searchSecureId OptionSearch Secure Id.
   */
  setOpenDocumentToArchiveDocument(
    documentId: number,
    documentSecureId: string,
    databaseId: number = this.databasesQuery.activeId,
    archiveId: number = this.archivesQuery.activeId,
    searchId: number = this.searchesQuery.activeId,
    searchSecureId: string = ''
  ): void {
    const changes: Partial<ApplicationState> = {
      openDocument: {
        type: 'archive',
        source: {
          databaseId,
          archiveId,
          searchId,
          searchSecureId,
          documentId,
          documentSecureId,
        },
      },
    };
    this.applicationStore.update(changes);
  }

  /**
   * Sets the open document to an inbox document.
   *
   * @param id Inbox Id.
   * @param filename Inbox filename.
   */
  setOpenDocumentToInboxDocument(id: number, filename: string): void {
    const changes: Partial<ApplicationState> = {
      openDocument: {
        type: 'inbox',
        source: {
          id,
          filename,
        },
      },
    };
    this.applicationStore.update(changes);
  }

  /**
   * Sets the open document to a task search document.
   *
   * @param documentId Document Id.
   * @param documentSecureId Document Secure Id.
   * @param databaseId Database Id. Defaults to the active database.
   * @param archiveId Archive Id. Defaults to the active archive.
   */
  setOpenDocumentToTaskSearchDocument(
    documentId: number,
    documentSecureId: string,
    databaseId: number = this.databasesQuery.activeId,
    archiveId: number = this.archivesQuery.activeId
  ): void {
    const changes: Partial<ApplicationState> = {
      openDocument: {
        type: 'taskSearch',
        source: {
          archiveId,
          databaseId,
          documentId,
          documentSecureId,
        },
      },
    };
    this.applicationStore.update(changes);
  }

  /**
   * Set the current PDF preview URL.
   *
   * @param url PDF document URL to display.
   */
  setPdfPreviewUrl(url: string, contentSearchTerm = '') {
    const changes: Partial<ApplicationState> = {
      pdfPreviewUrl: url,
      pdfPreviewContentSearchTerm: contentSearchTerm,
    };
    if (url !== '') changes.rightSidebarOpen = true;
    this.applicationStore.update(changes);
  }

  /**
   * Set the refresh results when closing document tabs setting.
   *
   * @param state New state.
   */
  setRefreshResultsWhenClosingDocumentTabs(state: boolean) {
    this.applicationStore.update({
      refreshResultsWhenClosingDocumentTabs: state,
    });
  }

  /**
   * Set the current visibility of the right sidebar.
   *
   * @param state New state.
   */
  setRightSidebarOpen(state: boolean) {
    this.applicationStore.update({ rightSidebarOpen: state });
  }

  /**
   * Sets the database ID for which the search store is currently loaded.
   * @param databaseId Database ID.
   */
  setSearchStoreDatabaseId(databaseId: string) {
    this.applicationStore.update({ searchStoreDatabaseId: databaseId });
  }

  /**
   * Sets the current visibility of the versions archive.
   *
   * @param state New state.
   */
  setShowVersionsArchive(state: boolean) {
    this.applicationStore.update({ showVersionsArchive: state });
  }

  /**
   * Set the theme mode.
   *
   * @param themeMode New theme mode.
   */
  setThemeMode(themeMode: ThemeMode): void {
    this.applicationStore.update({ themeMode: themeMode });
  }

  /**
   * Sets the usePreviousSearchCriteriaInRelatedSearch setting.
   *
   * @param state Whether or not the setting should be enabled.
   */
  setUsePreviousSearchCriteriaInRelatedSearch(state: boolean) {
    this.applicationStore.update({
      usePreviousSearchCriteriaInRelatedSearch: state,
    });
  }

  /**
   * Set current user.
   *
   * @param user New user value.
   */
  setUser(user: User) {
    this.applicationStore.update({ user });
  }

  /**
   * Set the auto save setting.
   *
   * @param on Whether or not auto save should be enabled.
   */
  setViewerAutoSave(on: boolean) {
    this.applicationStore.update({ viewerAutoSave: on });
  }

  /**
   * Sets the width of the viewer right sidebar.
   *
   * @param newWidth New sidebar width in pixels or undefined to use the default.
   */
  setViewerRightSidebarWidth(newWidth: number | undefined) {
    this.applicationStore.update({ viewerRightSidebarWidth: newWidth });
  }

  /**
   * Sets the width of the viewer right sidebar when a table field is open.
   *
   * @param newWidth New sidebar width for table field in pixels or undefined to use the default
   */
  setViewerRightSidebarTableFieldWidth(newWidth: number | undefined) {
    this.applicationStore.update({
      viewerRightSidebarTableFieldWidth: newWidth,
    });
  }

  /** Set a workflow action button override. */
  setUserActionButtonOverride(
    action: string,
    enabled: boolean,
    message: string = ''
  ): void {
    this.logger.debug(
      `Setting workflow action button override for '${action}' to enabled=${enabled}, message='${message}'`
    );
    this.applicationStore.update((state) => {
      const newOverrides = { ...state.userActionButtonStateOverrides };
      newOverrides[action] = { enabled, message };
      return { userActionButtonStateOverrides: newOverrides };
    });
  }

  /**
   * Invert the current stored value of archiveNavSectionExpanded.
   */
  toggleArchiveNavSectionExpanded() {
    this.logger.debug('Toggling archive navigation section expanded.');
    this.applicationStore.update((state) => ({
      archiveNavSectionExpanded: !state.archiveNavSectionExpanded,
    }));
  }

  /**
   * Invert the current stored value of favoriteSearchesExpanded.
   */
  toggleFavoriteSearchNavSectionExpanded(): void {
    this.logger.debug('Toggling favorite search navigation section expanded.');
    this.applicationStore.update((state) => ({
      favoriteSearchesExpanded: !state.favoriteSearchesExpanded,
    }));
  }

  /**
   * INvert the current stored value of indexerSidebarOpen.
   */
  toggleIndexerSidebarOpen(): void {
    this.applicationStore.update((state) => ({
      indexerSidebarOpen: !state.indexerSidebarOpen,
    }));
  }

  /**
   * Invert the current stored value of leftSidebarNavHidden.
   */
  toggleLeftSidebarNav() {
    this.logger.debug('Toggling left sidebar navigation expanded.');
    this.applicationStore.update((state) => ({
      leftSidebarNavHidden: !state.leftSidebarNavHidden,
    }));
  }

  /**
   * Invert the current stored value of rightSidebarOpen.
   */
  toggleRightSidebarOpen() {
    this.logger.debug('Toggling right sidebar open state.');
    this.applicationStore.update((state) => ({
      rightSidebarOpen: !state.rightSidebarOpen,
    }));
  }

  /**
   * Invert the current stored value of taskSearchSectionExpanded.
   */
  toggleTaskSearchSectionExpanded() {
    this.logger.debug('Toggling task search section expanded.');
    this.applicationStore.update((state) => ({
      taskSearchSectionExpanded: !state.taskSearchSectionExpanded,
    }));
  }

  /**
   * Invert the current stored value of archiveNavSectionExpanded.
   */
  toggleinboxNavSectionExpanded() {
    this.logger.debug('Toggling inbox navigation section expanded.');
    this.applicationStore.update((state) => ({
      inboxNavSectionExpanded: !state.inboxNavSectionExpanded,
    }));
  }

  /** Invert the current stored value of useCompactTableFieldGrid. */
  toggleUseCompactTableFieldGrid() {
    this.logger.debug('Toggling use compact table field grid setting.');
    this.applicationStore.update((state) => ({
      useCompactTableFieldGrid: !state.useCompactTableFieldGrid,
    }));
  }
}
